'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { BubbleActions } from '@/components/chat/BubbleBlock';
import { saveFromChat, CHAT_TAP_OFFLINE } from '@/lib/chat/block-actions';
import { openExternalUrl } from '@/lib/native/external-link';
import { isNative } from '@/lib/native/is-native';
import { openOAuthSession } from '@/lib/native/oauth-browser';
import { useVoiceOptional } from '@/components/voice/VoiceProvider';
import { holdCallOpen, releaseCallHold } from '@/lib/voice/call-hold';
import { ORDER_STAGE_EVENT, hasStageCard, noteStageCard, orderEndedTurn, orderStage, orderStageLine, type OrderStageDetail } from '@/lib/chat/order-ended';
import {
  FINAL_ORDER_STATUSES,
  lineLabel,
  poundsFromPence,
  statusLine,
  whereLabel,
  type SupplierOrderBlock,
  type SupplierOrderState,
  type OrderStage,
} from '@/lib/chat/blocks/supplier-order';

/**
 * A supplier order: the basket to approve, then the order followed live
 * (`supplier-order.ts`).
 *
 * Approve opens pay.mucka.ai with a one-time ticket, where the owner approves
 * this ceiling with a passkey. Nothing on this card can spend money: it opens
 * the sheet, answers a question the run asked, or stops the order, and the
 * server checks the order's state for each of those.
 *
 * The state is read from the server, not the stored block, so a card scrolled
 * back to still tells the truth. It is read every few seconds while something
 * is happening, and when the page comes back into view (the owner has just
 * been on the pay sheet or in the bank's app).
 */
const POLL_MS = 4000;

export function SupplierOrder({ block, initialState, announce = true }: {
  block: SupplierOrderBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
  /** What the opener already knows (the Orders list in Settings), so the card
   *  starts from the truth rather than from "waiting for approval". */
  initialState?: SupplierOrderState;
  /** Say in the chat (or on the call) how the order ended. Off where the card
   *  is not in the chat (the Orders list in Settings). */
  announce?: boolean;
}) {
  const [state, setState] = useState<SupplierOrderState | null>(initialState ?? null);
  /** No buttons until the card knows what state the order is in: it used to
   *  show Approve for a moment on every order, finished ones too. */
  const [known, setKnown] = useState(!!initialState);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState<'approve' | 'cancel' | 'answer' | null>(null);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const path = `/api/supplier-orders/${encodeURIComponent(block.orderId)}`;

  const read = useCallback(async () => {
    try {
      const res = await fetch(path);
      if (res.ok) setState((await res.json()) as SupplierOrderState);
    } catch {
      // The next tick tries again; the card keeps what it last knew.
    } finally {
      setKnown(true);
    }
  }, [path]);

  const voice = useVoiceOptional();
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  /** Approve was tapped during a live call, so the call is held open until it ends. */
  const toldCall = useRef(false);
  /** The step this card first saw the order at; a later step is news. */
  const firstStage = useRef<OrderStage | null>(null);
  const announced = useRef(new Set<OrderStage>());
  /** The last step this card posted a card for: state, so this card redraws as a line. */
  const [postedStage, setPostedStage] = useState<OrderStage | null>(null);

  const status = state?.status ?? 'awaiting_approval';
  const final = FINAL_ORDER_STATUSES.has(status);
  const mine: OrderStage = block.stage ?? 'prepared';
  const current: OrderStage = known && state ? orderStage(state) : mine;
  if (known && firstStage.current === null) firstStage.current = current;
  /** A later step has its own card below: this one shrinks to a line. Never
   *  in the Orders list in Settings, where one card is the whole story. */
  if (mine !== 'prepared') noteStageCard(block.orderId, mine);
  const superseded = announce && known && current !== mine
    && (postedStage === current || hasStageCard(block.orderId, current));

  // The order moved on while this card watched: post the next step's card,
  // with Mucka's line, once per order and step (lib/chat/order-ended.ts). At
  // the end, lift any hold, and on a call hand the agent the outcome as a turn
  // so it says it out loud. Never for a step the order had already reached
  // when the card opened (a reload, or the Orders list).
  useEffect(() => {
    if (!state || !known || firstStage.current === null || current === firstStage.current || announced.current.has(current)) return;
    announced.current.add(current);
    if (current === 'done' && toldCall.current) { toldCall.current = false; releaseCallHold(); }
    if (!announce) return;
    if (current === 'done') {
      const v = voiceRef.current;
      if (v?.active) v.sendUserAction(orderEndedTurn(block, state));
    }
    window.dispatchEvent(new CustomEvent<OrderStageDetail>(ORDER_STAGE_EVENT, {
      detail: { orderId: block.orderId, stage: current, text: orderStageLine(current, block, state), block: { ...block, stage: current } },
    }));
    setPostedStage(current);
  }, [current, known, state, block, announce]);
  useEffect(() => {
    if (final && toldCall.current) { toldCall.current = false; releaseCallHold(); }
  }, [final]);
  const moving = status === 'approved' || status === 'running' || status === 'needs_answer' || (opened && status === 'awaiting_approval');

  useEffect(() => { void read(); }, [read]);
  useEffect(() => {
    if (final || !moving || superseded) return;
    const t = setInterval(() => { if (document.visibilityState === 'visible') void read(); }, POLL_MS);
    return () => clearInterval(t);
  }, [final, moving, superseded, read]);
  useEffect(() => {
    if (final) return;
    const back = () => { if (document.visibilityState === 'visible') void read(); };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    return () => { document.removeEventListener('visibilitychange', back); window.removeEventListener('focus', back); };
  }, [final, read]);

  const approve = async () => {
    setBusy('approve');
    setError(null);
    try {
      const res = await fetch('/api/supplier-orders/pay-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose: 'approve_order', orderId: block.orderId }),
      });
      const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!res.ok || !body.url) {
        setError(body.error ?? 'Could not open the approval screen. Try again.');
        return;
      }
      // On a call, the passkey page takes them away for a minute or two: keep
      // the call open and quiet until the order ends (lib/voice/call-hold.ts).
      if (voice?.active) {
        toldCall.current = true;
        holdCallOpen(10 * 60_000, `They are approving the ${block.merchant} order with their passkey.`);
        voice.sendContextualUpdate(`They have gone to the passkey page to approve the ${block.merchant} order, and cannot talk to you while they are there. Say nothing until they speak to you again: do not ask whether they are still there, and do not end the call. Anything you hear meanwhile is them using that page.`);
      }
      if (isNative()) {
        // The in-app browser closes itself once the approval has landed, as
        // the card page does. Opened plain, its Back to Mucka could not close
        // it, and the approved owner was left on a page with nothing to tap
        // (Tom, 3 Nov 2026).
        setOpened(true);
        void openOAuthSession(`${body.url}&app=1`, {
          closeWhen: async () => {
            const r = await fetch(path, { cache: 'no-store' }).catch(() => null);
            const s = r?.ok ? ((await r.json()) as SupplierOrderState).status : null;
            return !!s && s !== 'awaiting_approval';
          },
        }).then(() => { void read(); });
      } else {
        await openExternalUrl(body.url);
        setOpened(true);
      }
    } catch (err) {
      Sentry.captureMessage('[supplier-ordering] could not open the approval sheet', { level: 'warning', extra: { err: String(err) } });
      setError(CHAT_TAP_OFFLINE);
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    setBusy('cancel');
    setError(null);
    const out = await saveFromChat('POST', path, { action: 'cancel' });
    setBusy(null);
    if (!out.ok) { setError(out.message); return; }
    if ((out.data as { stopping?: boolean } | undefined)?.stopping) setStopping(true);
    void read();
  };

  /** `yes` answers every yes/no field; typed boxes send what was typed. */
  const answer = async (yes?: boolean) => {
    const q = state?.question;
    if (!q) return;
    setBusy('answer');
    setError(null);
    const sent: Record<string, string | boolean> = { ...answers };
    if (yes !== undefined) for (const f of q.fields) if (f.boolean) sent[f.key] = yes;
    const out = await saveFromChat('POST', path, { action: 'answer', requestId: q.requestId, answers: sent });
    setBusy(null);
    if (!out.ok) { setError(out.message); return; }
    setAnswers({});
    void read();
  };

  const q = status === 'needs_answer' ? state?.question ?? null : null;
  const passwordAsked = !!q?.fields.some((f) => f.protected);
  /** Every field a yes/no: answered with two buttons, not a box to type in. */
  const yesNo = !!q && q.fields.length > 0 && q.fields.every((f) => f.boolean);
  const ordered = status === 'ordered';

  // An earlier step's card, once a later one has been posted: one line saying
  // where the order is now, and nothing to press (the live card is below).
  if (superseded) {
    return (
      <div className="mchat-rcpt mchat-sord mchat-sord--past" role="group" aria-label={`${block.merchant} order, earlier step`}>
        <div className="mchat-rcpt-head">
          <span className="mchat-rcpt-headline">
            <span className="mchat-rcpt-lead">{block.rehearsal ? `${block.merchant} order (rehearsal)` : `${block.merchant} order`}</span>
            <span className="mchat-rcpt-meta">{statusLine(block, state)}</span>
          </span>
        </div>
      </div>
    );
  }
  /** A later step's card: the order and where it is, not the sums again. */
  const later = mine !== 'prepared';

  return (
    <div className="mchat-rcpt mchat-sord" role="group" aria-label={`${block.merchant} order`}>
      <div className="mchat-rcpt-head">
        <span className="mchat-rcpt-headline">
          <span className="mchat-rcpt-lead">
            {block.rehearsal ? `${block.merchant} order (rehearsal)` : `${block.merchant} order`}
          </span>
          <span className="mchat-rcpt-meta">{whereLabel(block)}</span>
        </span>
      </div>

      <div className="mchat-rcpt-lines">
        {block.lines.map((l, i) => (
          <div className="mchat-rcpt-line" key={`${l.code ?? i}-${i}`}>
            <span className="mchat-rcpt-desc">
              {lineLabel(l)}
              {l.code && <span className="mchat-rcpt-jobsub"> · {l.code}</span>}
            </span>
            <span className="mchat-rcpt-amount">{poundsFromPence(l.unitPence * l.quantity)}</span>
          </div>
        ))}
      </div>

      <div className="mchat-rcpt-sums">
        {!later && <span className="mchat-rcpt-sum"><span>Basket</span><span>{poundsFromPence(block.subtotalPence)}</span></span>}
        {!later && block.deliveryPence > 0 && (
          <span className="mchat-rcpt-sum"><span>Delivery, up to</span><span>{poundsFromPence(block.deliveryPence)}</span></span>
        )}
        {!later && <span className="mchat-rcpt-sum"><span>Room for a price change</span><span>{poundsFromPence(block.allowancePence)}</span></span>}
        <span className="mchat-rcpt-sum mchat-rcpt-sum--total">
          <span>{ordered ? 'Paid' : 'Up to'}</span>
          <span>{poundsFromPence(ordered && state?.totalPence != null ? state.totalPence : block.ceilingPence)}</span>
        </span>
      </div>

      <p className="mchat-rcpt-note">
        {status === 'awaiting_approval' ? `On your ${block.card}. ` : ''}
        {stopping && !final ? 'Stopping it…' : statusLine(block, state)}
      </p>

      {q && (
        <div className="mchat-rcpt-job" role="group" aria-label="The order's question">
          <span className="mchat-rcpt-label">It asks</span>
          <span className="mchat-rcpt-jobname">{q.question}</span>
          {passwordAsked ? (
            <p className="mchat-rcpt-note">This needs a password, which Mucka can&apos;t pass on yet. Stop the order and buy it yourself this time.</p>
          ) : (
            q.fields.filter((f) => !f.boolean).map((f) => (
              <div className="mucka-mod-field" key={f.key}>
                <label className="mucka-mod-field-label" htmlFor={`sord-${block.orderId}-${f.key}`}>{f.label}</label>
                <input
                  id={`sord-${block.orderId}-${f.key}`}
                  className="mucka-mod-input"
                  value={answers[f.key] ?? ''}
                  maxLength={300}
                  onChange={(e) => setAnswers((a) => ({ ...a, [f.key]: e.target.value }))}
                />
              </div>
            ))
          )}
        </div>
      )}

      {error && <p className="mchat-body mchat-error" role="alert">{error}</p>}

      {known && status === 'awaiting_approval' && (
        <BubbleActions
          actions={[
            { label: opened ? 'Open the approval again' : 'Approve with passkey', busyLabel: 'Opening…', busy: busy === 'approve', disabled: !!busy, tone: 'primary', onPress: () => { void approve(); } },
            { label: 'Cancel', busyLabel: 'Cancelling…', busy: busy === 'cancel', disabled: !!busy, tone: 'quiet', onPress: () => { void cancel(); } },
          ]}
        />
      )}
      {q && yesNo && (
        <BubbleActions
          actions={[
            { label: 'Yes', busyLabel: 'Sending…', busy: busy === 'answer', disabled: !!busy, tone: 'primary', onPress: () => { void answer(true); } },
            { label: 'No, stop it', busyLabel: 'Stopping…', busy: busy === 'cancel', disabled: !!busy || stopping, tone: 'destructive', onPress: () => { void cancel(); } },
          ]}
        />
      )}
      {q && !yesNo && (
        <BubbleActions
          actions={[
            ...(passwordAsked ? [] : [{
              label: q.fields.length ? 'Send' : 'I’ve done it',
              busyLabel: 'Sending…',
              busy: busy === 'answer',
              disabled: !!busy,
              tone: 'primary' as const,
              onPress: () => { void answer(); },
            }]),
            { label: 'Stop the order', busyLabel: 'Stopping…', busy: busy === 'cancel', disabled: !!busy || stopping, tone: 'destructive', onPress: () => { void cancel(); } },
          ]}
        />
      )}
      {(status === 'running' || status === 'approved') && (
        <BubbleActions
          actions={[{ label: 'Stop the order', busyLabel: 'Stopping…', busy: busy === 'cancel', disabled: !!busy || stopping, tone: 'destructive', onPress: () => { void cancel(); } }]}
        />
      )}
    </div>
  );
}
